/**
 * CommercialQAView — Lumen8 Content Manager QA Dashboard
 *
 * Reviews club-allocated TVC and LED content across all fixtures.
 * Grouped by Club → Round → TVC / LED sections.
 * Uses commercialAirtable service functions for approve/reject (avoids 422s).
 *
 * Dark theme matching AppShell (bg: #0a0a0f).
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import commercialAirtable from "../services/commercialAirtable";

// ─── Theme — matches AppShell dark theme ───
const T = {
  bg: "#0a0a0f",
  bgEl: "#12121a",
  bgCard: "#16161f",
  bgHover: "#1e1e2a",
  accent: "#2fd6b5",
  accentGlow: "rgba(47,214,181,0.15)",
  text: "#ffffff",
  textSecondary: "#a1a1aa",
  textMuted: "#71717a",
  textDim: "#52525b",
  border: "rgba(255,255,255,0.08)",
  borderHover: "rgba(255,255,255,0.15)",
  shadowSm: "0 2px 8px rgba(0,0,0,0.3)",
  radius: 10,
  radiusSm: 6,
};
const FONT = "'Inter', system-ui, sans-serif";

// ─── Club code → name mapping ───
const CLUB_NAMES = {
  ADE: "Adelaide", BRI: "Brisbane", CAR: "Carlton", COL: "Collingwood",
  ESS: "Essendon", FRE: "Fremantle", GCS: "Gold Coast", GEE: "Geelong",
  GWS: "GWS Giants", HAW: "Hawthorn", MEL: "Melbourne", NTH: "North Melbourne",
  PTA: "Port Adelaide", RIC: "Richmond", STK: "St Kilda", SYD: "Sydney",
  WCE: "West Coast", WBD: "Western Bulldogs",
};

/** Extract club code from content ID or name (e.g. "CAR_8VT1QCS" → "CAR", "COM-581" → fall back to name) */
const extractClub = (contentId, contentName) => {
  // Try contentId first (STK_xxx format)
  if (contentId) {
    const m = String(contentId).match(/^([A-Z]{2,4})_/);
    if (m && CLUB_NAMES[m[1]]) return m[1];
  }
  // Fall back to contentName (e.g. "STK_B9K7603_Chery_Match_Day_Partner_MCG-tiled")
  if (contentName) {
    const m = String(contentName).match(/^([A-Z]{2,4})_/);
    if (m && CLUB_NAMES[m[1]]) return m[1];
  }
  return "Unknown";
};
const clubName = (code) => CLUB_NAMES[code] || code;

const g = (v) => (Array.isArray(v) ? v[0] : v);
const str = (v) => { if (v == null) return ""; if (typeof v === "string") return v; if (Array.isArray(v)) return String(v[0] || ""); return String(v); };

const roundSortKey = (r) => {
  if (!r) return 999;
  const u = String(r).toUpperCase().trim();
  if (u === "OR") return 0;
  const m = u.match(/R?(\d+)/);
  return m ? parseInt(m[1], 10) : 50;
};

// ─── Tabs ───
const TABS = [
  { id: "all", label: "All Content" },
  { id: "pending", label: "Pending QA" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function CommercialQAView() {
  const [items, setItems] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedRound, setSelectedRound] = useState("ALL");
  const [selectedClub, setSelectedClub] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [actionInProgress, setActionInProgress] = useState(null);
  const [recentlyActioned, setRecentlyActioned] = useState(new Set()); // keep visible after action

  // ─── Fetch all club content slots ───
  const fetchData = useCallback(async () => {
    setRecentlyActioned(new Set()); // clear on refresh
    try {
      setLoading(true);
      setError(null);

      // ─── Parallel fetch: all 5 API calls at once instead of sequential ───
      const [fixturesRaw, reels, playlistsRaw, tvcSlotsRaw, ledSlotsRaw] = await Promise.all([
        commercialAirtable.getFixturesAsStrings().catch(() => []),
        commercialAirtable.getTVCReels().catch(() => []),
        commercialAirtable.getPlaylists().catch(() => []),
        // Server-side filter: only CLUB source TVC slots
        commercialAirtable.getAllTVCReelSlots({
          filterByFormula: '{Source} = "CLUB"',
        }).catch(() => []),
        // Server-side filter: only club type LED slots
        commercialAirtable.getAllPlaylistSlots({
          filterByFormula: '{Slot Type} = "club"',
        }).catch(() => []),
      ]);

      // 1. Fixtures — for round/venue resolution
      const fixMap = {};
      fixturesRaw.forEach((r) => {
        const f = r.fields;
        const roundName = str(f["Round Name"]) || str(f["Round"]) || "";
        const roundNum = f["Round Number"] ?? null;
        let round = "";
        if (roundName && !roundName.startsWith("rec")) round = roundName;
        else if (roundNum != null) round = roundNum === 0 || roundNum === "0" ? "OR" : "R" + roundNum;

        const fix = {
          round,
          venue: str(f["Venue Name"]) || str(f["Venue"]) || "",
          homeTeam: str(f["Home Team"]) || "",
          awayTeam: str(f["Away Team"]) || "",
        };

        fixMap[r.id] = fix;
        const name = str(f["Name"]) || "";
        const fixtureId = str(f["Fixture ID"]) || "";
        const matchId = str(f["Match ID"]) || "";
        if (name) fixMap[name] = fix;
        if (fixtureId) fixMap[fixtureId] = fix;
        if (matchId) fixMap[matchId] = fix;
      });
      console.log(`[CommercialQA] Fixture map: ${Object.keys(fixMap).length} keys from ${fixturesRaw.length} fixtures`);

      // 2. TVC Reels → fixture mapping
      const reelFixMap = {};
      const reelInfoMap = {};
      reels.forEach((r) => {
        const f = r.fields;
        const fixId = g(f["Fixture"]) || "";
        if (fixId) reelFixMap[r.id] = fixId;
        const reelRound = str(f["Round"]) || str(f["Round Name"]) || "";
        const reelVenue = str(f["Venue"]) || str(f["Venue Name"]) || "";
        if (reelRound || reelVenue) {
          reelInfoMap[r.id] = {
            round: (reelRound && !reelRound.startsWith("rec")) ? reelRound : "",
            venue: (reelVenue && !reelVenue.startsWith("rec")) ? reelVenue : "",
          };
        }
      });
      console.log(`[CommercialQA] ${Object.keys(reelFixMap).length} reel→fixture mappings, ${Object.keys(reelInfoMap).length} reels with direct round/venue`);

      // 3. LED Playlists → fixture mapping
      const playlistFixMap = {};
      playlistsRaw.forEach((r) => {
        const fixId = g(r.fields["Fixture"]) || "";
        if (fixId) playlistFixMap[r.id] = fixId;
      });

      const allItems = [];

      // 4. TVC reel slots — already filtered server-side to Source=CLUB
      tvcSlotsRaw.forEach((r) => {
        const f = r.fields;
        const reelId = g(f["Reel"]) || g(f["TVC Reel"]) || "";
        const fixDisplayName = reelFixMap[reelId] || "";
        const fixA = fixMap[fixDisplayName] || {};
        const reelInfo = reelInfoMap[reelId] || {};
        const slotRound = str(f["Round"]) || "";
        const slotVenue = str(f["Venue"]) || "";

        const round = fixA.round || reelInfo.round || slotRound;
        const venue = fixA.venue || reelInfo.venue || slotVenue;

        const contentId = str(f["Content ID"]) || str(f["Lumen8 ID"]) || "";
        allItems.push({
          id: r.id,
          type: "TVC",
          contentName: str(f["Asset Name"]) || str(f["Name"]) || "",
          contentId,
          client: str(f["Client Name"]) || str(f["Client"]) || "Club",
          duration: str(f["Duration"]) || "30",
          status: str(f["Content Status"]) || "Pending QA",
          round,
          venue,
          club: extractClub(contentId, str(f["Asset Name"]) || str(f["Name"]) || ""),
        });
      });

      // 5. LED playlist slots — already filtered server-side to Slot Type=club
      ledSlotsRaw.forEach((r) => {
        const f = r.fields;
        const playlistId = g(f["Playlist"]) || "";
        const fixId = playlistFixMap[playlistId] || "";
        const fix = fixMap[fixId] || {};
        const contentId = str(f["Content ID"]) || "";
        allItems.push({
          id: r.id,
          type: "LED",
          contentName: str(f["Content Name"]) || "",
          contentId,
          client: str(f["Client"]) || "Club",
          duration: str(f["Duration"]) || "30s",
          status: str(f["Content Status"]) || "Pending QA",
          breakName: str(f["Break"]) || "",
          round: fix.round || "",
          venue: fix.venue || "",
          club: extractClub(contentId, str(f["Content Name"]) || ""),
        });
      });

      // 6. Group by club + type + round + content name
      const grouped = {};
      allItems.forEach((item) => {
        const key = `${item.club}::${item.type}::${item.round}::${item.contentName || item.contentId}`;
        if (!grouped[key]) {
          grouped[key] = { ...item, count: 0, ids: [], breaks: new Set() };
        }
        grouped[key].count++;
        grouped[key].ids.push(item.id);
        if (item.breakName) grouped[key].breaks.add(item.breakName);
        // Use first non-empty status
        if (item.status && !grouped[key].status) grouped[key].status = item.status;
      });

      const groupedList = Object.values(grouped).map((item) => ({
        ...item,
        breakSummary: [...item.breaks].sort().join(", "),
      }));

      // Extract unique rounds + clubs
      const uniqueRounds = [...new Set(groupedList.map((i) => i.round).filter(Boolean))]
        .sort((a, b) => roundSortKey(a) - roundSortKey(b));
      const uniqueClubs = [...new Set(groupedList.map((i) => i.club).filter((c) => c !== "Unknown"))]
        .sort();

      setRounds(uniqueRounds);
      setClubs(uniqueClubs);
      setItems(groupedList);
      console.log(`[CommercialQA] ${allItems.length} total slots → ${groupedList.length} grouped, ${uniqueClubs.length} clubs, ${uniqueRounds.length} rounds`);
    } catch (err) {
      console.error("[CommercialQA] Fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ─── Approve — uses commercialAirtable service (no 422) ───
  const handleApprove = useCallback(async (item) => {
    if (actionInProgress) return;
    setActionInProgress(item.ids[0]);
    try {
      if (item.type === "TVC") {
        await commercialAirtable.updateTVCReelSlotStatus(item.ids, "Approved");
      } else {
        await commercialAirtable.updatePlaylistSlotStatus(item.ids, "Approved");
      }
      setItems((prev) =>
        prev.map((it) => it.ids[0] === item.ids[0] ? { ...it, status: "Approved" } : it)
      );
      setRecentlyActioned((prev) => new Set([...prev, item.ids[0]]));
    } catch (err) {
      console.error("[CommercialQA] Approve error:", err);
      alert(`Failed to approve: ${err.message}`);
    } finally {
      setActionInProgress(null);
    }
  }, [actionInProgress]);

  // ─── Reject — uses commercialAirtable service (no 422) ───
  const handleReject = useCallback(async (item) => {
    if (actionInProgress) return;
    if (!window.confirm(`Reject "${item.contentName}"? The club will need to delete and re-allocate.`)) return;
    setActionInProgress(item.ids[0]);
    try {
      if (item.type === "TVC") {
        await commercialAirtable.updateTVCReelSlotStatus(item.ids, "Rejected");
      } else {
        await commercialAirtable.updatePlaylistSlotStatus(item.ids, "Rejected");
      }
      setItems((prev) =>
        prev.map((it) => it.ids[0] === item.ids[0] ? { ...it, status: "Rejected" } : it)
      );
      setRecentlyActioned((prev) => new Set([...prev, item.ids[0]]));
    } catch (err) {
      console.error("[CommercialQA] Reject error:", err);
      alert(`Failed to reject: ${err.message}`);
    } finally {
      setActionInProgress(null);
    }
  }, [actionInProgress]);

  // ─── Unlock (revert approved → pending so club can modify) ───
  // For both LED and TVC: reverts Content Status to "Pending QA"
  // Records persist — content is not deleted.
  const handleUnlock = useCallback(async (item) => {
    if (actionInProgress) return;
    if (!window.confirm(`Unlock "${item.contentName}"?\n\nThis will revert to Pending QA and allow the club to modify/re-allocate.`)) return;
    setActionInProgress(item.ids[0]);
    try {
      if (item.type === "TVC") {
        await commercialAirtable.updateTVCReelSlotStatus(item.ids, "Pending QA");
      } else {
        // LED: revert status (NOT delete) — content persists
        await commercialAirtable.updatePlaylistSlotStatus(item.ids, "Pending QA");
      }
      // Update local state to reflect new status
      setItems((prev) =>
        prev.map((it) => it.ids[0] === item.ids[0] ? { ...it, status: "Pending QA" } : it)
      );
      setRecentlyActioned((prev) => new Set([...prev, item.ids[0]]));
    } catch (err) {
      console.error("[CommercialQA] Unlock error:", err);
      alert(`Failed to unlock: ${err.message}`);
    } finally {
      setActionInProgress(null);
    }
  }, [actionInProgress]);

  // ─── Filtering ───
  // Items that were just approved/rejected stay visible on current tab until refresh
  const filterByTab = (item) => {
    if (activeTab === "all") return true;
    if (recentlyActioned.has(item.ids[0])) return true; // keep visible after action
    if (activeTab === "pending") return item.status === "Pending QA" || !item.status;
    if (activeTab === "approved") return item.status === "Approved";
    if (activeTab === "rejected") return item.status === "Rejected";
    return true;
  };
  const filterByRound = (item) => selectedRound === "ALL" || item.round === selectedRound;
  const filterByClub = (item) => selectedClub === "ALL" || item.club === selectedClub;

  const filtered = useMemo(() =>
    items.filter(filterByTab).filter(filterByRound).filter(filterByClub),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, activeTab, selectedRound, selectedClub]
  );

  // Group filtered items by club → round → type
  const groupedView = useMemo(() => {
    const clubMap = {};
    filtered.forEach((item) => {
      const c = item.club || "Unknown";
      if (!clubMap[c]) clubMap[c] = {};
      const r = item.round || "Unassigned";
      if (!clubMap[c][r]) clubMap[c][r] = { TVC: [], LED: [] };
      clubMap[c][r][item.type].push(item);
    });
    // Sort clubs and rounds
    const sorted = Object.entries(clubMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([club, roundMap]) => ({
        club,
        clubLabel: clubName(club),
        rounds: Object.entries(roundMap)
          .sort(([a], [b]) => roundSortKey(a) - roundSortKey(b))
          .map(([round, types]) => ({ round, tvc: types.TVC, led: types.LED })),
      }));
    return sorted;
  }, [filtered]);

  // Tab counts (respecting club + round filters)
  const tabItems = items.filter(filterByRound).filter(filterByClub);
  const counts = {
    pending: tabItems.filter((i) => i.status === "Pending QA" || !i.status).length,
    approved: tabItems.filter((i) => i.status === "Approved").length,
    rejected: tabItems.filter((i) => i.status === "Rejected").length,
    all: tabItems.length,
  };

  // ─── Loading ───
  if (loading) {
    return (
      <div style={{ padding: 80, textAlign: "center", fontFamily: FONT }}>
        <div style={{ fontSize: 13, color: T.textMuted }}>Loading club content for QA…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 80, textAlign: "center", fontFamily: FONT }}>
        <div style={{ color: T.textSecondary, marginBottom: 16 }}>{error}</div>
        <button onClick={fetchData} style={{
          fontSize: 13, fontWeight: 600, color: "#111", background: T.accent,
          border: "none", borderRadius: T.radiusSm, padding: "8px 24px", cursor: "pointer", fontFamily: FONT,
        }}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT, minHeight: "100vh" }}>

      {/* ─── Header ─── */}
      <div style={S.header}>
        <div style={S.headerInner}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={S.headerIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
            <div>
              <h1 style={{ fontSize: 17, fontWeight: 700, color: T.text, margin: 0, letterSpacing: -0.3 }}>
                Commercial QA
              </h1>
              <p style={{ fontSize: 12, color: T.textMuted, margin: "2px 0 0" }}>
                Review and approve club-allocated TVC and LED content
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Club filter */}
            <span style={{ fontSize: 12, color: T.textMuted }}>Club:</span>
            <select value={selectedClub} onChange={(e) => setSelectedClub(e.target.value)} style={S.selectInput}>
              <option value="ALL">All Clubs</option>
              {clubs.map((c) => <option key={c} value={c}>{clubName(c)} ({c})</option>)}
            </select>

            {/* Round filter */}
            <span style={{ fontSize: 12, color: T.textMuted, marginLeft: 6 }}>Round:</span>
            <select value={selectedRound} onChange={(e) => setSelectedRound(e.target.value)} style={S.selectInput}>
              <option value="ALL">All Rounds</option>
              {rounds.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>

            <button onClick={fetchData} style={S.refreshBtn} title="Refresh data">↻</button>
          </div>
        </div>
      </div>

      {/* ─── Tabs ─── */}
      <div style={S.tabBar}>
        {TABS.map((tab) => {
          const active = activeTab === tab.id;
          const count = counts[tab.id] || 0;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              ...S.tab,
              color: active ? T.text : T.textMuted,
              borderBottomColor: active ? T.accent : "transparent",
            }}>
              <span>{tab.label}</span>
              <span style={{
                ...S.tabCount,
                background: active ? T.accentGlow : "rgba(255,255,255,0.04)",
                color: active ? T.accent : T.textDim,
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* ─── Content — grouped by Club → Round → TVC/LED ─── */}
      <div style={{ padding: "20px 24px 60px" }}>
        {groupedView.length === 0 ? (
          <div style={{ padding: "48px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{activeTab === "pending" ? "🎉" : "📭"}</div>
            <div style={{ fontSize: 13, color: T.textMuted }}>
              {activeTab === "pending" ? "No content pending QA — all clear!" : `No ${activeTab} items.`}
            </div>
          </div>
        ) : (
          groupedView.map((clubGroup) => (
            <div key={clubGroup.club} style={{ marginBottom: 28 }}>
              {/* Club header */}
              <div style={S.clubHeader}>
                <span style={S.clubBadge}>{clubGroup.club}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{clubGroup.clubLabel}</span>
              </div>

              {clubGroup.rounds.map((roundGroup) => (
                <div key={roundGroup.round} style={{ marginBottom: 16 }}>
                  {/* Round sub-header */}
                  <div style={S.roundHeader}>
                    <span style={S.roundBadge}>{roundGroup.round}</span>
                  </div>

                  {/* TVC items */}
                  {roundGroup.tvc.length > 0 && (
                    <ContentTable
                      type="TVC"
                      items={roundGroup.tvc}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onUnlock={handleUnlock}
                      actionInProgress={actionInProgress}
                    />
                  )}

                  {/* LED items */}
                  {roundGroup.led.length > 0 && (
                    <ContentTable
                      type="LED"
                      items={roundGroup.led}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onUnlock={handleUnlock}
                      actionInProgress={actionInProgress}
                    />
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================
// CONTENT CLASSIFICATION
// ============================================================
const parseContentType = (name) => {
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.includes("-360") || lower.includes("_360")) return "360";
  if (lower.includes("-tiled") || lower.includes("_tiled")) return "Tiled";
  if (lower.includes("-iptv") || lower.includes("_iptv")) return "IPTV";
  return "";
};

const CONTENT_TYPE_STYLE = {
  "360":   { color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd", label: "360°" },
  "Tiled": { color: "#0d9488", bg: "#ccfbf1", border: "#5eead4", label: "TILED" },
  "IPTV":  { color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc", label: "IPTV" },
};

const parseSource = (contentId) => {
  if (!contentId) return "";
  if (/^COM-\d+$/i.test(contentId)) return "Commercial";
  return "Club";
};

const SOURCE_STYLE = {
  Club:       { color: "#d97706", bg: "rgba(245,158,11,0.12)", border: "#fcd34d" },
  Commercial: { color: "#60a5fa", bg: "rgba(59,130,246,0.12)", border: "#93c5fd" },
};

// ============================================================
// CONTENT TABLE (reusable for TVC/LED within a round)
// ============================================================
function ContentTable({ type, items, onApprove, onReject, onUnlock, actionInProgress }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, paddingLeft: 2 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, fontFamily: "monospace",
          color: type === "TVC" ? "#a78bfa" : "#60a5fa",
          background: type === "TVC" ? "rgba(139,92,246,0.15)" : "rgba(59,130,246,0.15)",
        }}>{type}</span>
        <span style={{ fontSize: 11, color: T.textDim }}>
          {items.reduce((s, i) => s + i.count, 0)} slot{items.reduce((s, i) => s + i.count, 0) !== 1 ? "s" : ""}
        </span>
      </div>

      <div style={S.table}>
        <div style={S.colHeader}>
          <span style={{ flex: 1, minWidth: 0 }}>CONTENT</span>
          <span style={{ width: 100 }}>VENUE</span>
          <span style={{ width: 50, textAlign: "center" }}>SLOTS</span>
          {type === "LED" && <span style={{ width: 90 }}>BREAKS</span>}
          <span style={{ width: 85, textAlign: "center" }}>STATUS</span>
          <span style={{ width: 195, textAlign: "center" }}>ACTIONS</span>
        </div>

        {items.map((item) => {
          const isProcessing = actionInProgress === item.ids[0];
          const isPending = item.status === "Pending QA" || !item.status;
          const isApproved = item.status === "Approved";
          const isRejected = item.status === "Rejected";

          return (
            <div key={item.ids[0]} style={{
              ...S.row,
              borderLeftColor: isApproved ? "#22c55e" : isRejected ? "#ef4444" : isPending ? "#f59e0b" : "transparent",
              background: isApproved ? "rgba(34,197,94,0.03)" : isRejected ? "rgba(239,68,68,0.03)" : "transparent",
            }}>
              {/* Content info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 500, color: T.text,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {item.contentName || "Untitled"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: T.textMuted }}>
                    {item.contentId}
                    {item.client && item.client !== "Club" ? ` · ${item.client}` : ""}
                  </span>
                  {(() => {
                    const cType = parseContentType(item.contentName);
                    const cStyle = CONTENT_TYPE_STYLE[cType];
                    if (!cStyle) return null;
                    return (
                      <span style={{
                        display: "inline-block", padding: "1px 7px", fontSize: 10, fontWeight: 700,
                        borderRadius: 4, letterSpacing: "0.04em",
                        color: cStyle.color, background: cStyle.bg, border: `1px solid ${cStyle.border}`,
                      }}>
                        {cStyle.label}
                      </span>
                    );
                  })()}
                  {(() => {
                    const src = parseSource(item.contentId);
                    const sStyle = SOURCE_STYLE[src];
                    if (!sStyle) return null;
                    return (
                      <span style={{
                        display: "inline-block", padding: "1px 6px", fontSize: 10, fontWeight: 600,
                        borderRadius: 4,
                        color: sStyle.color, background: sStyle.bg, border: `1px solid ${sStyle.border}`,
                      }}>
                        {src}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* Venue */}
              <div style={{ width: 100, fontSize: 11, color: T.textSecondary }}>
                {item.venue || "—"}
              </div>

              {/* Slots */}
              <div style={{ width: 50, textAlign: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.accent, fontFamily: "monospace" }}>×{item.count}</span>
              </div>

              {/* Breaks (LED only) */}
              {type === "LED" && (
                <div style={{ width: 90, fontSize: 10, color: T.textSecondary }}>{item.breakSummary || "—"}</div>
              )}

              {/* Status */}
              <div style={{ width: 85, textAlign: "center" }}>
                {isPending && <span style={S.statusPending}>Pending</span>}
                {isApproved && <span style={S.statusApproved}>Approved</span>}
                {isRejected && <span style={S.statusRejected}>Rejected</span>}
              </div>

              {/* Actions */}
              <div style={{ width: 195, display: "flex", justifyContent: "center", gap: 8 }}>
                {isPending && (
                  <>
                    <button onClick={() => onApprove(item)} disabled={isProcessing}
                      style={{ ...S.approveBtn, opacity: isProcessing ? 0.5 : 1 }}>
                      ✓ Approve
                    </button>
                    <button onClick={() => onReject(item)} disabled={isProcessing}
                      style={{ ...S.rejectBtn, opacity: isProcessing ? 0.5 : 1 }}>
                      ✗ Reject
                    </button>
                  </>
                )}
                {isApproved && (
                  <button onClick={() => onUnlock(item)} disabled={isProcessing}
                    style={{ ...S.unlockBtn, opacity: isProcessing ? 0.5 : 1 }}>
                    🔓 Unlock
                  </button>
                )}
                {isRejected && (
                  <button onClick={() => onApprove(item)} disabled={isProcessing}
                    style={{ ...S.approveBtn, opacity: isProcessing ? 0.5 : 1 }}>
                    ↩ Re-approve
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const S = {
  header: { background: T.bgCard, borderBottom: `1px solid ${T.border}`, padding: "18px 24px" },
  headerInner: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
  headerIcon: {
    width: 38, height: 38, borderRadius: 10, background: T.accentGlow,
    display: "flex", alignItems: "center", justifyContent: "center", color: T.accent,
  },
  selectInput: {
    background: T.bgEl, border: `1px solid ${T.borderHover}`, borderRadius: T.radiusSm,
    color: T.text, fontSize: 12, fontWeight: 600, fontFamily: FONT,
    padding: "7px 30px 7px 12px", cursor: "pointer", outline: "none",
    appearance: "none", WebkitAppearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='rgba(255,255,255,0.4)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
  },
  refreshBtn: {
    width: 32, height: 32, borderRadius: T.radiusSm,
    background: T.bgEl, border: `1px solid ${T.borderHover}`,
    color: T.textSecondary, fontSize: 16, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT,
  },

  // Tabs
  tabBar: {
    display: "flex", gap: 0, padding: "0 24px",
    borderBottom: `1px solid ${T.border}`, background: T.bgCard,
  },
  tab: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "12px 20px", fontSize: 12, fontWeight: 600,
    background: "none", border: "none",
    borderBottom: "2px solid transparent", marginBottom: -1,
    cursor: "pointer", fontFamily: FONT,
  },
  tabCount: {
    fontSize: 10, fontWeight: 700, padding: "2px 8px",
    borderRadius: 10, minWidth: 22, textAlign: "center",
  },

  // Club + round grouping
  clubHeader: {
    display: "flex", alignItems: "center", gap: 10,
    marginBottom: 12, paddingLeft: 2,
  },
  clubBadge: {
    fontSize: 11, fontWeight: 700, color: "#fbbf24",
    background: "rgba(251,191,36,0.12)", padding: "3px 10px",
    borderRadius: 5, fontFamily: "monospace", letterSpacing: 0.5,
  },
  roundHeader: {
    display: "flex", alignItems: "center", gap: 8,
    marginBottom: 8, paddingLeft: 8,
  },
  roundBadge: {
    fontSize: 11, fontWeight: 700, color: T.accent,
    background: T.accentGlow, padding: "2px 10px",
    borderRadius: 4, fontFamily: "monospace",
  },

  // Table
  table: {
    borderRadius: T.radius, border: `1px solid ${T.border}`,
    overflow: "hidden", background: T.bgEl, boxShadow: T.shadowSm,
  },
  colHeader: {
    display: "flex", alignItems: "center", padding: "8px 16px",
    fontSize: 10, fontWeight: 700, color: T.textDim,
    letterSpacing: 1, textTransform: "uppercase",
    background: T.bgCard, borderBottom: `1px solid ${T.border}`,
    gap: 12, fontFamily: FONT,
  },
  row: {
    display: "flex", alignItems: "center", padding: "10px 16px",
    borderBottom: `1px solid ${T.border}`,
    borderLeft: "3px solid transparent",
    gap: 12, fontFamily: FONT,
  },

  // Status
  statusPending: { fontSize: 11, fontWeight: 700, color: "#fbbf24", background: "rgba(251,191,36,0.12)", padding: "3px 10px", borderRadius: 4 },
  statusApproved: { fontSize: 11, fontWeight: 700, color: "#4ade80", background: "rgba(74,222,128,0.12)", padding: "3px 10px", borderRadius: 4 },
  statusRejected: { fontSize: 11, fontWeight: 700, color: "#f87171", background: "rgba(248,113,113,0.12)", padding: "3px 10px", borderRadius: 4 },

  // Buttons
  approveBtn: {
    fontSize: 12, fontWeight: 700, color: "#fff", background: "#16a34a",
    border: "none", borderRadius: T.radiusSm, padding: "6px 16px",
    cursor: "pointer", fontFamily: FONT, boxShadow: "0 1px 4px rgba(22,163,74,0.3)",
  },
  rejectBtn: {
    fontSize: 12, fontWeight: 700, color: "#fff", background: "#dc2626",
    border: "none", borderRadius: T.radiusSm, padding: "6px 16px",
    cursor: "pointer", fontFamily: FONT, boxShadow: "0 1px 4px rgba(220,38,38,0.3)",
  },
  unlockBtn: {
    fontSize: 12, fontWeight: 700, color: T.text, background: "rgba(255,255,255,0.08)",
    border: `1px solid ${T.borderHover}`, borderRadius: T.radiusSm, padding: "6px 16px",
    cursor: "pointer", fontFamily: FONT,
  },
};
